/* eslint-disable indent */
type LazyLoadResourceParent =
  | 'body'
  | 'head'
  | {
      tag: keyof HTMLElementTagNameMap
      id: string
    }

const createOrGetParentElement = (parent: LazyLoadResourceParent) => {
  if (parent === 'body') return document.body

  if (parent === 'head') return document.head

  let parentElement = document.getElementById(parent.id)
  if (parentElement) return parentElement

  const { tag, id } = parent

  parentElement = document.createElement(tag)
  parentElement.id = id

  document.body.append(parentElement)

  return parentElement
}

interface LazyLoadResourceOption {
  type?: 'js' | 'css'
  parent?: LazyLoadResourceParent
}

type EndWith<T extends string, K extends string> = T extends `${infer F}${K}` ? true : false

/**
 * 자원이 필요할 때 로드합니다.
 * @param option.url 자원 url
 * @param option.type 자원 타입
 * @param parent 자원이 append 될 parent
 */
export const lazyLoadResource = <T extends string>(
  url: T,
  option?: LazyLoadResourceOption,
): Promise<EndWith<T, 'js'> extends true ? HTMLScriptElement : HTMLLinkElement> =>
  new Promise((resolve, reject) => {
    const type = (option?.type ?? url).endsWith('css') ? 'css' : 'js'
    const parent = option?.parent ?? (type === 'css' ? 'head' : 'body')
    const parentElement = createOrGetParentElement(parent)

    if (type === 'js') {
      let resourceElement: HTMLScriptElement | null = parentElement.querySelector(`script[src="${url}"]`)
      if (!resourceElement) {
        resourceElement = document.createElement('script')
        resourceElement.src = url
        resourceElement.async = true
        // @ts-ignore : 위의 조건문(type==="js")에서 auto narrowing이 안되고 있어서
        resourceElement.onload = () => resolve(resourceElement)
        resourceElement.onerror = (event, source, lineno, colno, error) => reject(error)
        parentElement.append(resourceElement)
      } else {
        // @ts-ignore : 위의 조건문(type==="js")에서 auto narrowing이 안되고 있어서
        resolve(resourceElement)
      }
    } else if (type === 'css') {
      let resourceElement: HTMLLinkElement | null = parentElement.querySelector(`link[href="${url}"]`)

      if (!resourceElement) {
        resourceElement = document.createElement('link')
        resourceElement.href = url
        resourceElement.type = 'text/css'
        resourceElement.rel = 'stylesheet'
        // @ts-ignore : 위의 조건문(type==="css")에서 auto narrowing이 안되고 있어서
        resourceElement.onload = () => resolve(resourceElement)
        resourceElement.onerror = (event, source, lineno, colno, error) => reject(error)
        parentElement.append(resourceElement)
      } else {
        // @ts-ignore : 위의 조건문(type==="js")에서 auto narrowing이 안되고 있어서
        resolve(resourceElement)
      }
    } else {
      reject(`Type is unsupported: ${type}, use one of ["js", "css"]`)
    }
  })
